import { useEffect, useRef, useState, useCallback } from 'react';
import type { Socket } from 'socket.io-client';
import { useSocket } from './useSocket';
import { normalizeSystemStats, LiveStats } from '../services/systemService';

const POLL_MS = 5000;
const HISTORY_LEN = 60;

export interface SystemStatsState {
  stats: LiveStats | null;
  netRate: { rx: number; tx: number };
  cpuHistory: number[];
  memHistory: number[];
}

/**
 * 三端统一系统资源数据源：
 * - 优先消费 Socket.IO 实时推送（system:stats），保证 web/桌面/移动三端数据同源、同步
 * - socket 不可用或断开时，自动降级到 HTTP 轮询 /api/system/stats
 * NeuralSynthesisMonitor 与移动端 SystemResourceCard 均调用本 Hook，彻底消除字段漂移
 */
export function useSystemStats(opts?: { socket?: Socket | null; pollMs?: number }): SystemStatsState {
  const fallback = useSocket();
  const socket = opts?.socket ?? fallback;
  const [socketReady, setSocketReady] = useState(false);
  const [stats, setStats] = useState<LiveStats | null>(null);
  const [netRate, setNetRate] = useState<{ rx: number; tx: number }>({ rx: 0, tx: 0 });
  const [cpuHistory, setCpuHistory] = useState<number[]>([]);
  const [memHistory, setMemHistory] = useState<number[]>([]);
  const prevNet = useRef<{ rx: number; tx: number; t: number } | null>(null);

  const ingest = useCallback((raw: any, src: 'socket' | 'http') => {
    if (!raw) return;
    const s = normalizeSystemStats(raw);
    setStats(s);
    setCpuHistory((h) => {
      const n = [...h, s.cpu_percent];
      return n.length > HISTORY_LEN ? n.slice(-HISTORY_LEN) : n;
    });
    setMemHistory((h) => {
      const n = [...h, s.memory_percent];
      return n.length > HISTORY_LEN ? n.slice(-HISTORY_LEN) : n;
    });
    const now = Date.now();
    if (prevNet.current && s.net) {
      const dt = (now - prevNet.current.t) / 1000;
      if (dt > 0) {
        const rxb = Math.max(0, s.net.rx_bytes - prevNet.current.rx) / dt;
        const txb = Math.max(0, s.net.tx_bytes - prevNet.current.tx) / dt;
        setNetRate({ rx: rxb, tx: txb });
      }
    }
    if (s.net) prevNet.current = { rx: s.net.rx_bytes, tx: s.net.tx_bytes, t: now };
  }, []);

  // 跟踪 socket 连接状态：连接成功则信任推送，暂停 HTTP 轮询避免重复采样
  useEffect(() => {
    if (!socket) return;
    const onConnect = () => setSocketReady(true);
    const onDisconnect = () => setSocketReady(false);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    if (socket.connected) setSocketReady(true);
    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, [socket]);

  // Socket 实时推送
  useEffect(() => {
    if (!socket) return;
    const onStats = (raw: any) => ingest(raw, 'socket');
    socket.on('system:stats', onStats);
    socket.emit('system:stats:subscribe');
    return () => {
      socket.off('system:stats', onStats);
    };
  }, [socket, ingest]);

  // HTTP 轮询兜底（仅 socket 不可用时）
  useEffect(() => {
    const activeRef = { current: true };
    const poll = async () => {
      if (socketReady) return;
      try {
        const r = await fetch('/api/system/stats', { credentials: 'include' });
        if (!r.ok) return;
        const raw = await r.json();
        if (activeRef.current) ingest(raw, 'http');
      } catch {}
    };
    poll();
    const id = setInterval(poll, opts?.pollMs ?? POLL_MS);
    return () => {
      activeRef.current = false;
      clearInterval(id);
    };
  }, [ingest, socketReady, opts?.pollMs]);

  return { stats, netRate, cpuHistory, memHistory };
}
